/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/* eslint-disable no-shadow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { gnoment } from 'shared-ui/gnoment';
import {
    Button,
    ContentBlock,
    FlexCol,
    FlexRow,
    phoneValidator,
    SecurityHelper,
    Stack,
    zipCodeValidator,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { authModalActions } from 'shared-ui/auth/redux';

import {
    STANDARD,
    EXPEDITED,
    EXPEDITED_PRICE,
    STANDARD_PRICE,
    DATE_FORMAT,
    RESERVATIONS_ACTION_INSERT,
    RESERVATIONS_ACTION_UPDATE,
} from '../constants';
import { fetchPassType } from '../actions/passType';
import {
    createReservations,
    fetchReservation,
    updateReservation,
} from '../actions/reservation';
import { verifyAddress } from '../actions/address';
import { passTypeSelectors, reservationSelectors } from '../reducers';
import { ReservationDefaults } from '../props';
import Progress from '../components/IAOrderDetailsPage/Progress';
import Header from '../components/IAOrderDetailsPage/Header';
import HeaderPlaceholder from '../components/IAOrderDetailsPage/HeaderPlaceholder';
import PassTypes from '../components/IAOrderDetailsPage/PassTypes';
import Holders from '../components/IAOrderDetailsPage/Holders';
import NeedToKnow from '../components/IAOrderDetailsPage/NeedToKnow';
import CTABar from '../components/IAOrderDetailsPage/CTABar';
import Summary from '../components/IAOrderDetailsPage/Summary';
import SummaryModal from '../components/IAOrderDetailsPage/SummaryModal';
import SummaryPlaceholder from '../components/IAOrderDetailsPage/SummaryPlaceholder';
import AddressModal from '../components/IAOrderDetailsPage/AddressModal';
import { Divider, LeftRail, PageBody, PageSection, Row } from '../components/layout';

const steps = ['Pass Selection', 'Shopping Cart', 'Payment', 'Confirmation'];
const PASS_TYPE_ID = '7005cd6e-8386-458e-b4b1-8b382bf70889';

export class IAOrderDetailsPage extends Component {
    state = {
        action: '',
        errors: {},
        isAddressVerified: false,
        isAddressModalOpen: false,
        isConsentChecked: false,
        isLoading: true,
        isSubmitting: false,
        isSummaryModalOpen: false,
        passTypes: [],
        reservations: [],
        originalAddress: {},
        suggestedAddress: {},
    };

    componentDidMount() {
        if (!SecurityHelper.isLoggedIn()) {
            this.props.openLoginModal();
            return;
        }

        this.init();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.account !== this.props.account) {
            if (!this.props.account) {
                return;
            }

            this.init();
        }
    }

    onChangeHandler = (key = '', value = '') => {
        if (!key) {
            return;
        }

        const reservations = set(cloneDeep(this.state.reservations), key, value);

        this.setState({
            reservations,
        });
    };

    onChangeConsentHandler = (isConsentChecked = false) => {
        this.setState({
            isConsentChecked,
        });
    };

    onChangeQuantityHandler = (id = '', quantity = '') => {
        const { passTypes } = this.state;
        const passType = passTypes.find((item) => {
            return item?.id === id;
        });

        let reservations = cloneDeep(this.state.reservations);
        const reservationIndex = reservations.findIndex((item) => {
            return item?.pass_type_id === id;
        });

        if (!quantity) {
            reservations = reservations.filter((item) => {
                return item?.pass_type_id !== id;
            });
        } else if (reservationIndex !== -1) {
            reservations[reservationIndex].quantity = quantity;
        } else {
            reservations.push({
                ...cloneDeep(ReservationDefaults),
                pass_type_id: passType?.id,
                pass_type: cloneDeep(passType),
                quantity,
            });
        }

        this.setState({
            reservations,
        });
    };

    onChangeAddressHandler = (address) => {
        let { reservations } = this.state;

        reservations = reservations.map((item) => {
            // eslint-disable-next-line no-param-reassign
            item.shipping.Address = address;
            return item;
        });

        this.setState(
            {
                isAddressVerified: true,
                isAddressModalOpen: false,
                reservations,
            },
            () => {
                this.onSubmitHandler();
            }
        );
    };

    onSubmitHandler = () => {
        const { action, isAddressVerified } = this.state;
        const errors = this.validationHandler();
        const hasErrors = !isEmpty(errors);
        const requests = [];

        this.setState({
            isSummaryModalOpen: false,
            errors,
        });

        if (hasErrors) {
            return;
        }

        if (!isAddressVerified) {
            this.addressHandler();
            return;
        }

        this.setState({
            isSubmitting: true,
        });

        const reservations = this.getReservationsTransformed();
        const [reservation] = reservations;

        if (action === RESERVATIONS_ACTION_UPDATE) {
            const updateReservationRequest = this.props.updateReservation(reservation);
            requests.push(updateReservationRequest);
        } else {
            const createReservationRequest = this.props.createReservations(reservations);
            requests.push(createReservationRequest);
        }

        Promise.all(requests).then(() => {
            this.setState({
                isSubmitting: false,
            });

            window.location.assign('/cart');
        });
    };

    getPassType2020Transformed = (passType2020) => {
        const passType = cloneDeep(passType2020);
        passType.id = `${passType2020.id}-1`;
        passType.duration = '12 Months';
        passType.duration_description = 'Starts on purchase date';
        passType.name = 'Interagency Annual Pass';
        passType.img_url = '2023/atb-annual-pass-sq.png';
        passType.img_url_small = passType2020.img_url;
        passType.summary_start_date = gnoment().format(DATE_FORMAT);
        passType.summary_end_date = gnoment()
            .add(12, 'months')
            .endOf('month')
            .format(DATE_FORMAT);
        passType.holiday_season = false;
        return passType;
    };

    getReservationTotal = () => {
        const { reservations } = this.state;
        const hasReservations = !!reservations.length;

        if (!hasReservations) {
            return 0;
        }

        return reservations.reduce((accumulator, item) => {
            const price = item?.pass_type?.cost;
            const quantity = item?.quantity;
            const shipping =
                item?.shipping?.Method === EXPEDITED ? EXPEDITED_PRICE : STANDARD_PRICE;
            const totalPerItem = price * quantity + shipping;
            return accumulator + totalPerItem;
        }, 0);
    };

    getReservationsTransformed = () => {
        const { account } = this.props;
        return cloneDeep(this.state.reservations).map((value) => {
            const item = value;
            // eslint-disable-next-line no-param-reassign
            item.pass_type_id = item.pass_type_id.slice(0, item.pass_type_id.length - 2);
            // eslint-disable-next-line no-param-reassign
            item.pass_info.holiday_season = item?.pass_type?.holiday_season;
            item.pass_info.pass_name = item?.pass_type?.name;
            item.pass_cost = item?.pass_type?.cost;
            item.pass_sku = item?.pass_type?.sku;

            const shipping =
                item?.shipping?.Method === EXPEDITED ? EXPEDITED_PRICE : STANDARD_PRICE;
            item.shipping.Cost = shipping;

            item.account_id = account.account_id;
            // eslint-disable-next-line no-param-reassign
            delete item?.pass_type;
            return item;
        });
    };

    fetchPassType = (passTypeId) => {
        return this.props.fetchPassType(passTypeId);
    };

    fetchReservation = (reservationId) => {
        return this.props.fetchReservation(reservationId);
    };

    toggleSummaryModal = () => {
        const { isSummaryModalOpen } = this.state;
        this.setState({ isSummaryModalOpen: !isSummaryModalOpen });
    };

    toggleAddressModal = () => {
        const { isAddressModalOpen } = this.state;
        this.setState({ isAddressModalOpen: !isAddressModalOpen });
    };

    validationHandler = () => {
        const { isConsentChecked, passTypes, reservations } = this.state;
        const errors = {};

        reservations.forEach((item, index) => {
            const itemErrors = [];
            const passType = passTypes.find((passType) => {
                return passType?.id === item?.pass_type_id;
            });
            const zipCodeValidation = zipCodeValidator(item?.shipping?.Address?.zip_code);
            const phoneValidation = phoneValidator(
                item?.pass_info?.holder_info?.phone_number
            );

            if (!item?.pass_info?.holder_info?.first_name) {
                itemErrors.push({
                    errorText: 'First Name is required.',
                    inputId: `first-name-${index}`,
                });
            }

            if (!item?.pass_info?.holder_info?.last_name) {
                itemErrors.push({
                    errorText: 'Last Name is required.',
                    inputId: `last-name-${index}`,
                });
            }

            if (!item?.shipping?.Address?.address1) {
                itemErrors.push({
                    errorText: 'Address Line 1 is required.',
                    inputId: `address-line-1-${index}`,
                });
            }

            if (!item?.shipping?.Address?.city) {
                itemErrors.push({
                    errorText: 'City is required.',
                    inputId: `city-${index}`,
                });
            }

            if (!item?.shipping?.Address?.state) {
                itemErrors.push({
                    errorText: 'State is required.',
                    inputId: `state-${index}`,
                });
            }

            if (!item?.shipping?.Address?.zip_code) {
                itemErrors.push({
                    errorText: 'ZIP Code is required.',
                    inputId: `zip-code-${index}`,
                });
            } else if (!zipCodeValidation?.isValid) {
                itemErrors.push({
                    errorText: `${zipCodeValidation?.errorText}.`,
                    inputId: `zip-code-${index}`,
                });
            }

            if (!item?.pass_info?.holder_info?.phone_number) {
                itemErrors.push({
                    errorText: 'Phone Number is required.',
                    inputId: `phone-number-${index}`,
                });
            } else if (!phoneValidation?.isValid) {
                itemErrors.push({
                    errorText: `${phoneValidation?.errorText}.`,
                    inputId: `phone-number-${index}`,
                });
            }

            const hasItemErrors = !!itemErrors.length;

            if (hasItemErrors) {
                errors[passType?.name] = itemErrors;
            }
        });

        if (!isConsentChecked) {
            errors.General = [
                {
                    errorText:
                        'Please read and agree to the important Need to Know information.',
                    inputId: 'consent',
                },
            ];
        }

        return errors;
    };

    addressHandler = () => {
        this.setState({
            isLoading: true,
        });

        const { passTypes } = this.state;
        const reservations = this.getReservationsTransformed();
        const [reservation] = reservations;
        const originalAddress = cloneDeep(reservation?.shipping?.Address);

        this.props
            .verifyAddress(originalAddress)
            .then((suggestedAddress) => {
                // eslint-disable-next-line no-param-reassign
                delete suggestedAddress?.country;

                if (
                    suggestedAddress?.address1 === '' &&
                    suggestedAddress?.address2 !== ''
                ) {
                    // eslint-disable-next-line no-param-reassign
                    suggestedAddress.address1 = suggestedAddress?.address2;
                    // eslint-disable-next-line no-param-reassign
                    suggestedAddress.address2 = '';
                }

                if (isEqual(suggestedAddress, originalAddress)) {
                    this.setState(
                        {
                            isAddressVerified: true,
                            isLoading: false,
                        },
                        () => {
                            this.onSubmitHandler();
                        }
                    );
                } else {
                    this.setState({
                        isAddressModalOpen: true,
                        isLoading: false,
                        originalAddress,
                        suggestedAddress,
                    });
                }
            })
            .catch(() => {
                const errors = {};
                reservations.forEach((item, index) => {
                    const itemErrors = [];
                    const passType = passTypes.find((passType) => {
                        return passType?.id === item?.pass_type_id;
                    });

                    itemErrors.push({
                        errorText:
                            'It appears there is an error in the address you provided.',
                        inputId: `address-line-1-${index}`,
                    });

                    errors[passType?.name] = itemErrors;
                });

                this.setState({
                    errors,
                    isLoading: false,
                });
            });
    };

    init = () => {
        const reservationId = this.props.router.params?.resId;
        const action = reservationId
            ? RESERVATIONS_ACTION_UPDATE
            : RESERVATIONS_ACTION_INSERT;
        const requests = [];
        const reservations = [];

        if (action === RESERVATIONS_ACTION_UPDATE) {
            const fetchReservationRequest = this.fetchReservation(reservationId);
            requests.push(fetchReservationRequest);
        }

        Promise.all(requests).then((responses) => {
            const reservation = responses?.[0];
            const passTypeId = reservation?.pass_type_id
                ? reservation?.pass_type_id
                : PASS_TYPE_ID;

            this.fetchPassType(passTypeId).then((response) => {
                const passTypes = [];
                const passType2020 = this.getPassType2020Transformed(response);

                if (action === RESERVATIONS_ACTION_UPDATE) {
                    passTypes.push(passType2020);
                    reservation.pass_type_id = `${reservation.pass_type_id}-1`;
                    reservation.pass_type = passType2020;
                    reservations.push(reservation);
                } else {
                    passTypes.push(passType2020);

                    if (!this.props.iaPassesGovShutdown) {
                        reservations.push({
                            ...cloneDeep(ReservationDefaults),
                            pass_type_id: passType2020?.id,
                            pass_type: cloneDeep(passType2020),
                            quantity: 1,
                            shipping: {
                                ...cloneDeep(ReservationDefaults.shipping),
                                Method: STANDARD,
                            },
                        });
                    }
                }

                this.setState({
                    action,
                    isLoading: false,
                    passTypes,
                    reservations,
                });
            });
        });
    };

    render() {
        const {
            action,
            errors,
            isAddressModalOpen,
            isConsentChecked,
            isLoading,
            isSubmitting,
            isSummaryModalOpen,
            passTypes,
            reservations,
            originalAddress,
            suggestedAddress,
        } = this.state;
        const total = this.getReservationTotal();

        if (!this.props.account) {
            return (
                <PageBody>
                    <PageSection>
                        <Row>
                            <LeftRail>
                                <h2>Order Details</h2>
                                <Divider />
                                <p>Before you buy a pass, you must sign in.</p>
                                <Button
                                    name="sign-in"
                                    size="lg"
                                    className="force-sign-in-button"
                                    onClick={() => this.props.openLoginModal()}
                                >
                                    Sign Up / Log In
                                </Button>
                            </LeftRail>
                        </Row>
                    </PageSection>
                </PageBody>
            );
        }

        if (isLoading) {
            return (
                <main id="page-body" className="ia-order-details-page">
                    <Progress activeStep={1} steps={steps} />
                    <ContentBlock className="mt-5 mb-5">
                        <Stack>
                            <FlexRow>
                                <FlexCol lg={8}>
                                    <HeaderPlaceholder />
                                </FlexCol>
                                <FlexCol lg={4} className="ia-order-details-summary">
                                    <SummaryPlaceholder />
                                </FlexCol>
                            </FlexRow>
                        </Stack>
                    </ContentBlock>
                </main>
            );
        }

        return (
            <main id="page-body" className="ia-order-details-page">
                <Progress activeStep={1} steps={steps} />
                <ContentBlock className="mt-5 mb-5">
                    <Stack>
                        <FlexRow>
                            <FlexCol lg={8}>
                                <Stack space="xl">
                                    <Header errors={errors} />
                                    <PassTypes
                                        onChangeQuantityHandler={
                                            this.onChangeQuantityHandler
                                        }
                                        passTypes={passTypes}
                                        reservations={reservations}
                                    />
                                    <Holders
                                        errors={errors}
                                        onChangeHandler={this.onChangeHandler}
                                        reservations={reservations}
                                    />
                                    <NeedToKnow
                                        errors={errors}
                                        isConsentChecked={isConsentChecked}
                                        onChangeConsentHandler={
                                            this.onChangeConsentHandler
                                        }
                                    />
                                    <CTABar
                                        action={action}
                                        onSubmitHandler={this.onSubmitHandler}
                                        onShowSummaryModal={this.toggleSummaryModal}
                                        isSubmitting={isSubmitting}
                                        reservations={reservations}
                                    />
                                </Stack>
                            </FlexCol>
                            <FlexCol lg={4} className="ia-order-details-summary">
                                <Summary
                                    action={action}
                                    isSubmitting={isSubmitting}
                                    onSubmitHandler={this.onSubmitHandler}
                                    passTypes={passTypes}
                                    reservations={reservations}
                                    total={total}
                                />
                            </FlexCol>
                        </FlexRow>
                    </Stack>
                    <SummaryModal
                        isSubmitting={isSubmitting}
                        isSummaryModalOpen={isSummaryModalOpen}
                        onSubmitHandler={this.onSubmitHandler}
                        passTypes={passTypes}
                        reservations={reservations}
                        toggleSummaryModal={this.toggleSummaryModal}
                    >
                        <Summary
                            action={action}
                            isSubmitting={isSubmitting}
                            modifierClasses="modal"
                            onSubmitHandler={this.onSubmitHandler}
                            passTypes={passTypes}
                            reservations={reservations}
                            total={total}
                        />
                    </SummaryModal>
                    <AddressModal
                        onChangeAddressHandler={this.onChangeAddressHandler}
                        isAddressModalOpen={isAddressModalOpen}
                        toggleAddressModal={this.toggleAddressModal}
                        originalAddress={originalAddress}
                        suggestedAddress={suggestedAddress}
                    />
                </ContentBlock>
            </main>
        );
    }
}

IAOrderDetailsPage.propTypes = {
    account: PropTypes.object,
    openLoginModal: PropTypes.func.isRequired,
    createReservations: PropTypes.func.isRequired,
    fetchReservation: PropTypes.func.isRequired,
    updateReservation: PropTypes.func.isRequired,
    fetchPassType: PropTypes.func.isRequired,
    router: COMPAT_ROUTER_PROP_TYPES,
    iaPassesGovShutdown: PropTypes.bool,
    verifyAddress: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    account: state.auth && state.auth.account && state.auth.account.account,
    govtShutdown: get(state, 'auth.banners', []).find(
        ({ governmentShutdown }) => governmentShutdown
    ),
    passType: passTypeSelectors.getPassType(state),
    reservation: reservationSelectors.getReservation(state),
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchPassType,
            createReservations,
            fetchReservation,
            updateReservation,
            openLoginModal: authModalActions.openLoginModal,
            verifyAddress,
        },
        dispatch
    );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withCompatRouter(IAOrderDetailsPage));
